import React, { useState } from "react";
import { userAPI } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Alert, Card, PageHeader } from "../components/UI";
import { User, Mail, Phone, MapPin, Lock, Save } from "lucide-react";

function Section({ title, emoji, children }) {
  return (
    <Card className="p-5">
      <h2 className="font-800 text-brand-800 text-lg mb-4 flex items-center gap-2">
        <span>{emoji}</span> {title}
      </h2>
      {children}
    </Card>
  );
}

export default function SettingsPage() {
  const { user, updateUser } = useAuth();

  const [profile, setProfile] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    location: user?.location || "",
  });
  const [profileMsg, setProfileMsg] = useState({ type: "", text: "" });
  const [profileLoading, setProfileLoading] = useState(false);

  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwErrors, setPwErrors] = useState({});
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { setProfileMsg({ type: "error", text: "Name cannot be empty" }); return; }
    setProfileLoading(true);
    setProfileMsg({ type: "", text: "" });
    try {
      const res = await userAPI.updateProfile(profile);
      updateUser(res.data.user || profile);
      setProfileMsg({ type: "success", text: "✅ Profile updated successfully!" });
    } catch (err) {
      setProfileMsg({ type: "error", text: err?.response?.data?.message || "Update failed. Please try again." });
    } finally {
      setProfileLoading(false);
    }
  };

  const validatePw = () => {
    const e = {};
    if (!passwords.current_password) e.current_password = "Please enter your current password";
    if (!passwords.new_password || passwords.new_password.length < 6) e.new_password = "New password must be at least 6 characters";
    if (passwords.new_password !== passwords.confirm) e.confirm = "Passwords do not match";
    return e;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const errs = validatePw();
    if (Object.keys(errs).length) { setPwErrors(errs); return; }
    setPwErrors({});
    setPwLoading(true);
    setPwMsg({ type: "", text: "" });
    try {
      await userAPI.changePassword({ current_password: passwords.current_password, new_password: passwords.new_password });
      setPwMsg({ type: "success", text: "✅ Password changed successfully!" });
      setPasswords({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwMsg({ type: "error", text: err?.response?.data?.message || "Password change failed." });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="⚙️ My Profile" subtitle="Update your information and password" />

      <div className="flex flex-col gap-5">
        {/* Avatar card */}
        <Card className="p-5 bg-gradient-to-r from-brand-50 to-earth-50">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-brand-600 text-white text-3xl font-900 flex items-center justify-center shadow-md">
              {user?.avatar || user?.name?.[0] || "F"}
            </div>
            <div>
              <div className="font-900 text-brand-800 text-xl">{user?.name || "Farmer"}</div>
              <div className="text-brand-500 font-600">{user?.email}</div>
              <div className="mt-1">
                <span className={`text-xs font-800 px-2 py-0.5 rounded-full ${user?.plan === "premium" ? "bg-earth-200 text-earth-800" : "bg-brand-200 text-brand-800"}`}>
                  {user?.plan === "premium" ? "⭐ Premium Plan" : "🌱 Free Plan"}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Profile form */}
        <Section title="Personal Information" emoji="👤">
          {profileMsg.text && (
            <div className="mb-4">
              <Alert type={profileMsg.type} onDismiss={() => setProfileMsg({ type: "", text: "" })}>
                {profileMsg.text}
              </Alert>
            </div>
          )}
          <form onSubmit={handleProfileSave} className="flex flex-col gap-4">
            <Input label="Full Name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} icon={User} placeholder="Your full name" />
            <div>
              <label className="text-sm font-700 text-brand-800 mb-1 block">📧 Email Address</label>
              <div className="px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-500 font-600 text-base">
                {user?.email} <span className="text-xs text-gray-400">(cannot be changed)</span>
              </div>
            </div>
            <Input label="Phone Number" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} icon={Phone} placeholder="+254 700 000000" />
            <Input label="Location / Farm Area" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} icon={MapPin} placeholder="e.g. Nakuru, Kenya" />
            <Button type="submit" loading={profileLoading} className="w-full sm:w-auto">
              <Save size={16} /> Save Profile
            </Button>
          </form>
        </Section>

        {/* Password form */}
        <Section title="Change Password" emoji="🔒">
          {pwMsg.text && (
            <div className="mb-4">
              <Alert type={pwMsg.type} onDismiss={() => setPwMsg({ type: "", text: "" })}>
                {pwMsg.text}
              </Alert>
            </div>
          )}
          <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
            <Input label="Current Password" type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} error={pwErrors.current_password} icon={Lock} placeholder="Enter current password" />
            <Input label="New Password" type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} error={pwErrors.new_password} icon={Lock} placeholder="At least 6 characters" />
            <Input label="Confirm New Password" type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} error={pwErrors.confirm} icon={Lock} placeholder="Type new password again" />
            <Button type="submit" loading={pwLoading} variant="secondary" className="w-full sm:w-auto">
              🔑 Change Password
            </Button>
          </form>
        </Section>

        {/* Account info */}
        <Section title="Account Info" emoji="ℹ️">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-brand-50 rounded-xl p-3 border border-brand-100">
              <div className="text-brand-400 font-700 mb-0.5">Member Since</div>
              <div className="font-800 text-brand-800">{user?.joined ? new Date(user.joined).toLocaleDateString() : "—"}</div>
            </div>
            <div className="bg-brand-50 rounded-xl p-3 border border-brand-100">
              <div className="text-brand-400 font-700 mb-0.5">Account Type</div>
              <div className="font-800 text-brand-800">{user?.role === "premium" ? "Premium Farmer" : "Free Farmer"}</div>
            </div>
            <div className="bg-brand-50 rounded-xl p-3 border border-brand-100">
              <div className="text-brand-400 font-700 mb-0.5">User ID</div>
              <div className="font-800 text-brand-800 font-mono text-xs">{user?.id?.slice(-10)}</div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
